import { Loader } from "@/common/components/ui/loader";
import { isAuthenticated } from "@/common/utils/auth";
import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthQuery } from "@/common/api/services/auth";

interface PrivateRouteProps {
  children: ReactNode;
}

export const PrivateRoute = ({ children }: PrivateRouteProps) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuth, setIsAuth] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { profileQuery } = useAuthQuery();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authenticated = await isAuthenticated();
        setIsAuth(authenticated);

        if (authenticated) {
          queryClient.invalidateQueries({ queryKey: profileQuery.queryKey });
        } else {
          queryClient.clear();
        }
      } catch (error) {
        console.error("Authentication check failed:", error);
        setIsAuth(false);
        queryClient.clear();
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [location.pathname, queryClient, profileQuery]);

  if (isChecking) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader size="lg" />
          <p className="text-muted-foreground">
            {t("common.loading", "Loading...")}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuth) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};
